import React, { useState } from 'react';
import { Box, Typography, Grid, TextField, Button, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormComponentProps, DependentsData } from '../../types/forms';

const Dependents: React.FC<FormComponentProps<DependentsData>> = ({ data, onNext }) => {
  const [dependents, setDependents] = useState<DependentsData['dependents']>(() => {
    if (data?.dependents) {
      return data.dependents;
    }
    return [];
  });

  const addDependent = () => {
    setDependents([
      ...dependents,
      {
        id: `dep-${dependents.length}`,
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        ssn: '',
        relationship: '',
        livedWithYou: false,
        student: false,
        disabled: false,
        fullTimeStudent: false,
        under19: false,
        claimedByOther: false,
        childcareExpenses: ''
      }
    ]);
  };

  const removeDependent = (id: string) => {
    setDependents(dependents.filter(dep => dep.id !== id));
  };

  const updateDependent = (id: string, field: keyof DependentsData['dependents'][0], value: string | boolean) => {
    const updatedDependents = dependents.map(dep =>
      dep.id === id ? { ...dep, [field]: value } : dep
    );
    setDependents(updatedDependents);
    onNext({ dependents: updatedDependents });
  };

  return (
    <Box>
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 400 }}>
        Dependents
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph sx={{ fontWeight: 400 }}>
        Add any dependents you want to claim on your tax return.
      </Typography>
      <Grid container spacing={3}>
        {dependents.map((dependent) => (
          <Grid item xs={12} key={dependent.id}>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={dependent.firstName}
                    onChange={(e) => updateDependent(dependent.id, 'firstName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={dependent.lastName}
                    onChange={(e) => updateDependent(dependent.id, 'lastName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={dependent.dateOfBirth}
                    onChange={(e) => updateDependent(dependent.id, 'dateOfBirth', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="SSN"
                    value={dependent.ssn}
                    onChange={(e) => updateDependent(dependent.id, 'ssn', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Relationship"
                    value={dependent.relationship}
                    onChange={(e) => updateDependent(dependent.id, 'relationship', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Childcare Expenses"
                    type="number"
                    value={dependent.childcareExpenses}
                    onChange={(e) => updateDependent(dependent.id, 'childcareExpenses', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={dependent.livedWithYou}
                            onChange={(e) => updateDependent(dependent.id, 'livedWithYou', e.target.checked)}
                          />
                        }
                        label="Lived with you"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={dependent.student}
                            onChange={(e) => updateDependent(dependent.id, 'student', e.target.checked)}
                          />
                        }
                        label="Student"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={dependent.disabled}
                            onChange={(e) => updateDependent(dependent.id, 'disabled', e.target.checked)}
                          />
                        }
                        label="Disabled"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={dependent.fullTimeStudent}
                            onChange={(e) => updateDependent(dependent.id, 'fullTimeStudent', e.target.checked)}
                          />
                        }
                        label="Full-time student"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={dependent.under19}
                            onChange={(e) => updateDependent(dependent.id, 'under19', e.target.checked)}
                          />
                        }
                        label="Under 19"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={dependent.claimedByOther}
                            onChange={(e) => updateDependent(dependent.id, 'claimedByOther', e.target.checked)}
                          />
                        }
                        label="Claimed by another person"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={1}>
                  <IconButton
                    color="error"
                    onClick={() => removeDependent(dependent.id)}
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Button
            variant="outlined"
            onClick={addDependent}
            sx={{ mt: 2 }}
          >
            Add Dependent
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dependents; 